import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import {
  Server,
  FolderOpen,
  FileText,
  Download,
  RefreshCw,
  Loader2,
  Check,
  X,
  ChevronRight,
  ArrowLeft,
  Database,
  Clock,
  Search,
  Filter,
  AlertCircle
} from "lucide-react";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const StatusBadge = ({ status }) => {
  const colors = {
    'Examination': 'bg-blue-100 text-blue-700 border-blue-200',
    'Registered': 'bg-emerald-100 text-emerald-700 border-emerald-200',
    'Published': 'bg-purple-100 text-purple-700 border-purple-200',
    'Application Published': 'bg-purple-100 text-purple-700 border-purple-200',
    'Opposed': 'bg-orange-100 text-orange-700 border-orange-200',
    'Dead': 'bg-slate-100 text-slate-500 border-slate-200',
    'Removed': 'bg-red-100 text-red-600 border-red-200',
    'Expired': 'bg-amber-100 text-amber-600 border-amber-200',
    'Pre-Publication': 'bg-indigo-100 text-indigo-700 border-indigo-200',
  };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 text-xs font-mono border ${colors[status] || 'bg-slate-100 text-slate-600 border-slate-200'}`}>
      {status}
    </span>
  );
};

const ImportStatusBadge = ({ status }) => {
  const colors = {
    'completed': 'bg-emerald-100 text-emerald-700',
    'downloading': 'bg-blue-100 text-blue-700',
    'parsing': 'bg-amber-100 text-amber-700',
    'failed': 'bg-red-100 text-red-700',
    'started': 'bg-blue-100 text-blue-700',
    'skipped': 'bg-slate-100 text-slate-600',
  };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 text-xs font-medium rounded ${colors[status] || 'bg-slate-100 text-slate-600'}`}>
      {status === 'completed' && <Check className="w-3 h-3 mr-1" />}
      {status === 'failed' && <X className="w-3 h-3 mr-1" />}
      {(status === 'downloading' || status === 'parsing') && <Loader2 className="w-3 h-3 mr-1 animate-spin" />}
      {status}
    </span>
  );
};

export const SFTPTest = () => {
  const [tab, setTab] = useState("dashboard");
  const [connectionStatus, setConnectionStatus] = useState(null);
  const [testing, setTesting] = useState(false);
  const [stats, setStats] = useState(null);
  const [browserPath, setBrowserPath] = useState("licensee/Increment");
  const [browserItems, setBrowserItems] = useState([]);
  const [browserLoading, setBrowserLoading] = useState(false);
  const [pathHistory, setPathHistory] = useState(["licensee/Increment"]);
  const [importing, setImporting] = useState(null);
  const [importPolling, setImportPolling] = useState(null);
  const [records, setRecords] = useState([]);
  const [recordsTotal, setRecordsTotal] = useState(0);
  const [recordsLoading, setRecordsLoading] = useState(false);
  const [recordsSearch, setRecordsSearch] = useState("");
  const [recordsStatus, setRecordsStatus] = useState("all");
  const [recordsPage, setRecordsPage] = useState(0);

  const token = localStorage.getItem('auth_token');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchStats = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/sftp/stats`, { headers });
      setStats(res.data);
    } catch (err) {
      console.error("Stats error:", err);
    }
  }, []);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  const testConnection = async () => {
    setTesting(true);
    try {
      const res = await axios.post(`${API}/sftp/test-connection`, {}, { headers });
      setConnectionStatus(res.data);
      if (res.data.status === 'connected') toast.success("SFTP connected!");
      else toast.error(res.data.message);
    } catch (err) {
      toast.error("Connection failed");
      setConnectionStatus({ status: 'error', message: err.response?.data?.detail || 'Failed' });
    } finally {
      setTesting(false);
    }
  };

  const browsePath = async (path) => {
    setBrowserLoading(true);
    try {
      const res = await axios.get(`${API}/sftp/browse?path=${encodeURIComponent(path)}`, { headers });
      setBrowserItems(res.data.items || []);
      setBrowserPath(path);
      setPathHistory(prev => [...prev, path]);
    } catch (err) {
      toast.error("Failed to browse directory");
    } finally {
      setBrowserLoading(false);
    }
  };

  const goBack = () => {
    if (pathHistory.length > 1) {
      const newHistory = [...pathHistory];
      newHistory.pop();
      const prevPath = newHistory[newHistory.length - 1];
      setPathHistory(newHistory);
      browsePath(prevPath);
    }
  };

  const startImport = async (filePath) => {
    setImporting(filePath);
    try {
      const res = await axios.post(`${API}/sftp/import`, { file_path: filePath }, { headers });
      if (res.data.status === 'skipped') {
        toast.info(res.data.message);
        setImporting(null);
        return;
      }
      toast.success("Import started!");
      // Poll for status
      const importId = res.data.import_id;
      const poll = setInterval(async () => {
        try {
          const statusRes = await axios.get(`${API}/sftp/import-status/${importId}`, { headers });
          setImportPolling(statusRes.data);
          if (statusRes.data.status === 'completed' || statusRes.data.status === 'failed') {
            clearInterval(poll);
            setImporting(null);
            setImportPolling(null);
            fetchStats();
            if (statusRes.data.status === 'completed') {
              toast.success(`Import complete! ${statusRes.data.records_new} new, ${statusRes.data.records_updated} updated`);
            } else {
              toast.error("Import failed: " + (statusRes.data.error || "Unknown error"));
            }
          }
        } catch (e) {
          console.error(e);
        }
      }, 3000);
    } catch (err) {
      toast.error("Failed to start import");
      setImporting(null);
    }
  };

  const fetchRecords = useCallback(async () => {
    setRecordsLoading(true);
    try {
      const params = new URLSearchParams({ limit: '50', skip: (recordsPage * 50).toString() });
      if (recordsStatus !== 'all') params.append('status', recordsStatus);
      if (recordsSearch) params.append('search', recordsSearch);
      const res = await axios.get(`${API}/sftp/records?${params}`, { headers });
      setRecords(res.data.records || []);
      setRecordsTotal(res.data.total || 0);
    } catch (err) {
      toast.error("Failed to load records");
    } finally {
      setRecordsLoading(false);
    }
  }, [recordsPage, recordsStatus, recordsSearch]);

  useEffect(() => {
    if (tab === 'records') fetchRecords();
  }, [tab, fetchRecords]);

  return (
    <div className="space-y-6" data-testid="sftp-test-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-2xl font-bold text-slate-900">SFTP Data Import</h1>
            <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded font-medium">TEST</span>
          </div>
          <p className="text-slate-500 text-sm mt-1">
            UK IPO SFTP data import and processing (separate from existing data)
          </p>
        </div>
        <Button onClick={testConnection} disabled={testing} variant="outline" className="gap-2">
          {testing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Server className="w-4 h-4" />}
          Test Connection
        </Button>
      </div>

      {/* Connection Status */}
      {connectionStatus && (
        <div className={`rounded-lg p-4 ${connectionStatus.status === 'connected' ? 'bg-emerald-50 border border-emerald-200' : 'bg-red-50 border border-red-200'}`}>
          <div className="flex items-center gap-2">
            {connectionStatus.status === 'connected' ? <Check className="w-5 h-5 text-emerald-600" /> : <X className="w-5 h-5 text-red-600" />}
            <span className={`font-medium ${connectionStatus.status === 'connected' ? 'text-emerald-700' : 'text-red-700'}`}>
              {connectionStatus.message}
            </span>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 bg-slate-100 p-1 rounded-lg w-fit">
        {[
          { id: 'dashboard', label: 'Dashboard', icon: Database },
          { id: 'browser', label: 'File Browser', icon: FolderOpen },
          { id: 'records', label: 'Imported Records', icon: FileText },
        ].map(t => (
          <button
            key={t.id}
            onClick={() => { setTab(t.id); if (t.id === 'browser' && browserItems.length === 0) browsePath(browserPath); }}
            className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              tab === t.id ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'
            }`}
          >
            <t.icon className="w-4 h-4" />
            {t.label}
          </button>
        ))}
      </div>

      {/* Dashboard Tab */}
      {tab === 'dashboard' && (
        <div className="space-y-6">
          {/* Stats Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <motion.div className="swiss-card" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 bg-teal-100 flex items-center justify-center"><Database className="w-5 h-5 text-teal-600" /></div>
                <span className="text-sm text-slate-500 uppercase tracking-wider">Total Records</span>
              </div>
              <p className="text-3xl font-black text-teal-600">{stats?.total_records?.toLocaleString() || 0}</p>
              <p className="text-xs text-slate-400 mt-1">in sftp_ukipo_applications</p>
            </motion.div>
            <motion.div className="swiss-card" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}>
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 bg-blue-100 flex items-center justify-center"><Clock className="w-5 h-5 text-blue-600" /></div>
                <span className="text-sm text-slate-500 uppercase tracking-wider">Last Import</span>
              </div>
              <p className="text-sm font-medium text-slate-900">
                {stats?.recent_imports?.[0]?.completed_at
                  ? new Date(stats.recent_imports[0].completed_at).toLocaleString('en-GB')
                  : 'No imports yet'}
              </p>
              <p className="text-xs text-slate-400 mt-1">
                {stats?.recent_imports?.[0]?.file_path?.split('/').pop() || '—'}
              </p>
            </motion.div>
            <motion.div className="swiss-card" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 bg-purple-100 flex items-center justify-center"><FileText className="w-5 h-5 text-purple-600" /></div>
                <span className="text-sm text-slate-500 uppercase tracking-wider">Imports Run</span>
              </div>
              <p className="text-3xl font-black text-purple-600">{stats?.recent_imports?.length || 0}</p>
              <p className="text-xs text-slate-400 mt-1">recent imports</p>
            </motion.div>
          </div>

          {/* Status Breakdown */}
          {stats?.statuses && Object.keys(stats.statuses).length > 0 && (
            <div className="swiss-card">
              <h3 className="font-semibold text-slate-900 mb-4">Records by Status</h3>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {Object.entries(stats.statuses).sort((a, b) => b[1] - a[1]).map(([status, count]) => (
                  <div key={status} className="bg-slate-50 rounded-lg p-3 text-center">
                    <StatusBadge status={status} />
                    <p className="text-xl font-bold text-slate-900 mt-2">{count.toLocaleString()}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Import History */}
          {stats?.recent_imports && stats.recent_imports.length > 0 && (
            <div className="swiss-card">
              <h3 className="font-semibold text-slate-900 mb-4">Import History</h3>
              <div className="space-y-2">
                {stats.recent_imports.map((log, i) => (
                  <div key={i} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <ImportStatusBadge status={log.status} />
                      <span className="text-sm font-mono text-slate-700">{log.file_path?.split('/').pop()}</span>
                    </div>
                    <div className="flex items-center gap-4 text-xs text-slate-500">
                      {log.records_new !== undefined && <span>{log.records_new} new</span>}
                      {log.records_updated !== undefined && <span>{log.records_updated} updated</span>}
                      {log.completed_at && <span>{new Date(log.completed_at).toLocaleString('en-GB')}</span>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Info */}
          <div className="swiss-card bg-blue-50 border-blue-200">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-blue-800">
                <p className="font-medium mb-1">Test Area</p>
                <p>This imports data into a separate <code className="bg-blue-100 px-1 rounded">sftp_ukipo_applications</code> collection. It does not affect the existing admin area data.</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* File Browser Tab */}
      {tab === 'browser' && (
        <div className="swiss-card">
          {/* Path breadcrumb */}
          <div className="flex items-center gap-2 mb-4">
            <Button variant="outline" size="sm" onClick={goBack} disabled={pathHistory.length <= 1 || browserLoading}>
              <ArrowLeft className="w-4 h-4" />
            </Button>
            <div className="flex items-center gap-1 text-sm text-slate-500 font-mono bg-slate-50 px-3 py-1.5 rounded flex-1">
              <Server className="w-3.5 h-3.5 mr-1" />
              /{browserPath}
            </div>
            <Button variant="outline" size="sm" onClick={() => browsePath(browserPath)} disabled={browserLoading}>
              <RefreshCw className={`w-4 h-4 ${browserLoading ? 'animate-spin' : ''}`} />
            </Button>
          </div>

          {/* Import progress */}
          {importing && importPolling && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
              <div className="flex items-center gap-3 mb-2">
                <Loader2 className="w-5 h-5 text-blue-600 animate-spin" />
                <span className="font-medium text-blue-700">Importing: {importing.split('/').pop()}</span>
              </div>
              <div className="text-sm text-blue-600 space-y-1">
                <p>Status: {importPolling.status}</p>
                {importPolling.records_processed > 0 && <p>Processed: {importPolling.records_processed} records ({importPolling.records_new} new, {importPolling.records_updated} updated)</p>}
                {importPolling.file_size_mb && <p>File size: {importPolling.file_size_mb} MB</p>}
              </div>
            </div>
          )}

          {/* File listing */}
          {browserLoading && browserItems.length === 0 ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
            </div>
          ) : (
            <div className="space-y-1">
              {browserItems.map((item, i) => (
                <div key={i} className="flex items-center justify-between p-3 hover:bg-slate-50 rounded-lg border border-transparent hover:border-slate-200 transition-colors">
                  <button
                    onClick={() => item.is_dir ? browsePath(`${browserPath}/${item.name}`) : null}
                    className={`flex items-center gap-3 flex-1 text-left ${item.is_dir ? 'cursor-pointer' : 'cursor-default'}`}
                  >
                    {item.is_dir ? (
                      <FolderOpen className="w-5 h-5 text-amber-500" />
                    ) : (
                      <FileText className="w-5 h-5 text-slate-400" />
                    )}
                    <div>
                      <p className={`text-sm font-medium ${item.is_dir ? 'text-slate-900' : 'text-slate-700'}`}>{item.name}</p>
                      <p className="text-xs text-slate-400">
                        {item.is_dir ? 'Directory' : `${item.size_mb} MB`}
                        {item.modified && ` — ${new Date(item.modified).toLocaleDateString('en-GB')}`}
                      </p>
                    </div>
                    {item.is_dir && <ChevronRight className="w-4 h-4 text-slate-400" />}
                  </button>
                  {!item.is_dir && item.name.endsWith('.zip') && (
                    <Button
                      size="sm"
                      onClick={() => startImport(`${browserPath}/${item.name}`)}
                      disabled={!!importing}
                      className="gap-1.5 bg-teal-600 hover:bg-teal-700 text-white"
                    >
                      <Download className="w-3.5 h-3.5" />
                      Import
                    </Button>
                  )}
                </div>
              ))}
              {browserItems.length === 0 && !browserLoading && (
                <p className="text-center text-slate-400 py-8">Empty directory</p>
              )}
            </div>
          )}
        </div>
      )}

      {/* Imported Records Tab */}
      {tab === 'records' && (
        <div className="space-y-4">
          {/* Filters */}
          <div className="swiss-card">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Search by name, applicant, or application number..."
                  value={recordsSearch}
                  onChange={(e) => setRecordsSearch(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && fetchRecords()}
                  className="pl-10"
                />
              </div>
              <Select value={recordsStatus} onValueChange={(v) => { setRecordsStatus(v); setRecordsPage(0); }}>
                <SelectTrigger className="w-48">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value="Examination">Examination</SelectItem>
                  <SelectItem value="Application Published">Published</SelectItem>
                  <SelectItem value="Pre-Publication">Pre-Publication</SelectItem>
                  <SelectItem value="Opposed">Opposed</SelectItem>
                  <SelectItem value="Registered">Registered</SelectItem>
                  <SelectItem value="Dead">Dead</SelectItem>
                  <SelectItem value="Removed">Removed</SelectItem>
                  <SelectItem value="Expired">Expired</SelectItem>
                </SelectContent>
              </Select>
              <Button onClick={fetchRecords} className="gap-2">
                <Filter className="w-4 h-4" /> Apply
              </Button>
            </div>
          </div>

          {/* Results */}
          <div className="swiss-card">
            <div className="flex items-center justify-between mb-4">
              <p className="text-sm text-slate-500">{recordsTotal.toLocaleString()} records found</p>
            </div>
            {recordsLoading ? (
              <div className="flex items-center justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
            ) : records.length === 0 ? (
              <div className="text-center py-12">
                <Database className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500">No records found</p>
                <p className="text-sm text-slate-400">Import data from the File Browser tab</p>
              </div>
            ) : (
              <div className="space-y-2">
                {records.map((rec, i) => (
                  <div key={i} className="border border-slate-200 rounded-lg p-4 hover:border-slate-300 transition-colors">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <p className="font-semibold text-slate-900 truncate">{rec.name || '(No name)'}</p>
                          <StatusBadge status={rec.status} />
                        </div>
                        <p className="text-xs font-mono text-slate-400 mb-2">{rec.application_number}</p>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs text-slate-600">
                          <div><span className="text-slate-400">Applicant:</span> {rec.applicant_name || '—'}</div>
                          <div><span className="text-slate-400">Type:</span> {rec.applicant_type || '—'}</div>
                          <div><span className="text-slate-400">Town:</span> {rec.applicant_town || '—'}</div>
                          <div><span className="text-slate-400">Country:</span> {rec.applicant_country || '—'}</div>
                        </div>
                        {rec.classes && rec.classes.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-2">
                            {rec.classes.map((c, ci) => (
                              <span key={ci} className="text-xs bg-teal-50 text-teal-700 px-1.5 py-0.5 rounded">Class {c}</span>
                            ))}
                          </div>
                        )}
                        {rec.representative_name && (
                          <p className="text-xs text-slate-400 mt-1">Rep: {rec.representative_name}</p>
                        )}
                      </div>
                      <div className="text-right text-xs text-slate-400 flex-shrink-0">
                        {rec.has_image && <span className="inline-block bg-slate-100 px-1.5 py-0.5 rounded mb-1">Has Logo</span>}
                        <p>{rec.application_date?.slice(0, 10)}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Pagination */}
            {recordsTotal > 50 && (
              <div className="flex items-center justify-between mt-4 pt-4 border-t border-slate-200">
                <p className="text-sm text-slate-500">
                  Showing {recordsPage * 50 + 1} - {Math.min((recordsPage + 1) * 50, recordsTotal)} of {recordsTotal}
                </p>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => setRecordsPage(p => Math.max(0, p - 1))} disabled={recordsPage === 0}>Previous</Button>
                  <Button variant="outline" size="sm" onClick={() => setRecordsPage(p => p + 1)} disabled={(recordsPage + 1) * 50 >= recordsTotal}>Next</Button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SFTPTest;
